import { useEffect, useRef, useCallback } from "react";
import type { Section } from "../../features/questionnaire";

export const useSectionNavigation = (
  currentSectionIndex: number,
  setCurrentSectionIndex: (index: number) => void,
  fallbackSection = 0, // lastVisited section or 0
  sections: Section[] = [],
) => {
  const SECTION_KEY = "section";
  const initialized = useRef(false);

  const getSectionFromUrl = useCallback(() => {
    const section = parseInt(
      new URLSearchParams(window.location.search).get(SECTION_KEY) ?? "",
      10,
    );

    if (isNaN(section) || section < 0 || section >= sections.length) {
      return fallbackSection;
    }

    return section;
  }, [fallbackSection, sections.length]);

  const setUrlSection = (
    section: number,
    method: "pushState" | "replaceState",
  ) => {
    const params = new URLSearchParams(window.location.search);
    params.set(SECTION_KEY, section.toString());

    window.history[method](null, "", `${window.location.pathname}?${params}`);
  };

  useEffect(() => {
    if (!sections.length) {
      return;
    }

    const sectionInUrl = getSectionFromUrl();

    if (!initialized.current) {
      if (sectionInUrl !== currentSectionIndex) {
        setCurrentSectionIndex(sectionInUrl);
      }
      setUrlSection(sectionInUrl, "replaceState");
      initialized.current = true;
    } else if (sectionInUrl !== currentSectionIndex) {
      setUrlSection(currentSectionIndex, "pushState");
    }
  }, [
    fallbackSection,
    sections.length,
    setCurrentSectionIndex,
    getSectionFromUrl,
    currentSectionIndex,
  ]);

  useEffect(() => {
    const onPopState = () => {
      const section = getSectionFromUrl();
      if (section !== currentSectionIndex) {
        setCurrentSectionIndex(section);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [currentSectionIndex, setCurrentSectionIndex, getSectionFromUrl]);
};
